import { PayloadAction, createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import { Order } from 'types/api/orders.type';

import type { RootState } from '../store';

const ordersAdapter = createEntityAdapter({
  selectId: (orders: Order) => orders?.orderNumber,
});

type ExtraState = {
  isLoading: boolean;
  // Cached full list used by the settings/top-bar dropdown. This is intentionally
  // separate from the entity adapter (which mirrors the paginated Orders list page).
  dropdownOrders: Order[];
  // Fingerprint of the scope the dropdown list was fetched for. Used to skip
  // refetching when navigating between pages.
  dropdownOrdersKey: string | null;
  isDropdownOrdersLoading: boolean;
};

export const ordersSlice = createSlice({
  name: 'orders',
  initialState: ordersAdapter.getInitialState<ExtraState>({
    isLoading: false,
    dropdownOrders: [],
    dropdownOrdersKey: null,
    isDropdownOrdersLoading: false,
  }),
  reducers: {
    setOrders(state, action) {
      ordersAdapter.setAll(state, action.payload);
    },
    setIsOrdersLoading(state, action) {
      state.isLoading = action.payload;
    },
    setDropdownOrders(state, action: PayloadAction<{ orders: Order[]; key: string }>) {
      state.dropdownOrders = action.payload.orders;
      state.dropdownOrdersKey = action.payload.key;
    },
    setIsDropdownOrdersLoading(state, action: PayloadAction<boolean>) {
      state.isDropdownOrdersLoading = action.payload;
    },
    invalidateDropdownOrders(state) {
      state.dropdownOrdersKey = null;
    },
  },
});

export const getState = (rootState: RootState) => rootState.orders;

export const ordersSelector = ordersAdapter.getSelectors((state: RootState) => state.orders);

export const getDropdownOrders = (rootState: RootState) => rootState.orders.dropdownOrders;
export const getDropdownOrdersKey = (rootState: RootState) =>
  rootState.orders.dropdownOrdersKey;

export const {
  setOrders,
  setIsOrdersLoading,
  setDropdownOrders,
  setIsDropdownOrdersLoading,
  invalidateDropdownOrders,
} = ordersSlice.actions;
